import React from "react";
import { Ticket, Clock, User, MoreHorizontal } from "lucide-react";
import {
  EntityGrid,
  EntityCard,
  StatusPill,
  IconButton,
} from "../../../design-system";
import styles from "./TicketCardGrid.module.scss";

/**
 * Tickets kart görünümü — list-2/grid-3/grid-4 modları için.
 * InvoiceCardGrid sözleşmesini takip eder (EntityCard/EntityGrid); ticket
 * alanlarına (durum/tür/atanan/aktivite) uyarlanmıştır.
 */

// Durum → cal- renk anahtarı (accent + StatusPill colorMap için tek kaynak).
const STATUS_TO_COLOR = {
  New: "blue",
  Open: "yellow",
  "In Progress": "violet",
  Pending: "yellow",
  Resolved: "emerald",
  Closed: "graphite",
  Reopened: "coral",
};

const buildPillColorMap = () =>
  Object.fromEntries(
    Object.entries(STATUS_TO_COLOR).map(([status, color]) => [
      status,
      {
        bg: `var(--cal-${color}-soft)`,
        fg: `var(--cal-${color}-fg)`,
        border: "transparent",
      },
    ])
  );

const PILL_COLOR_MAP = buildPillColorMap();

const brandInitials = (name) => {
  if (!name) return "?";
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");
};

const TicketCardGrid = ({ tickets = [], viewMode = "grid-3", onOpen, onMore }) => {
  const density = viewMode === "list-2" ? "compact" : "comfortable";

  return (
    <EntityGrid
      mode={viewMode}
      emptyState={
        <>
          <Ticket size={32} className={styles.emptyIcon} aria-hidden />
          <span>Ticket yok — yukarıdaki + ile ekleyebilirsin.</span>
        </>
      }
    >
      {tickets.map((ticket) => {
        const statusColor = STATUS_TO_COLOR[ticket.status] || "graphite";
        const labelList = Array.isArray(ticket.labels)
          ? ticket.labels
          : ticket.label
          ? [ticket.label]
          : [];
        const ticketTitle =
          ticket.title && ticket.title !== "-" ? ticket.title : "Başlıksız ticket";
        const clientName =
          ticket.client && ticket.client !== "-" ? ticket.client : ticketTitle;

        return (
          <EntityCard
            key={ticket.id}
            density={density}
            onClick={() => onOpen?.(ticket)}
            accentColor={statusColor}
            logo={
              <span
                className={styles.brandFallback}
                data-color={statusColor}
                aria-hidden
              >
                {brandInitials(clientName)}
              </span>
            }
            title={ticketTitle}
            subtitle={
              ticket.client && ticket.client !== "-"
                ? `Ticket #${ticket.id} · ${ticket.client}`
                : `Ticket #${ticket.id}`
            }
            actions={
              onMore ? (
                <IconButton
                  size="sm"
                  variant="ghost"
                  ariaLabel="Daha fazla"
                  onClick={(event) => {
                    event.stopPropagation();
                    onMore(ticket);
                  }}
                >
                  <MoreHorizontal size={14} />
                </IconButton>
              ) : null
            }
            meta={
              density === "comfortable"
                ? [
                    {
                      label: "Durum",
                      value: (
                        <StatusPill
                          value={ticket.status || "New"}
                          colorMap={PILL_COLOR_MAP}
                          interactive={false}
                          size="xs"
                        />
                      ),
                    },
                    ticket.type && ticket.type !== "-"
                      ? {
                          label: "Tür",
                          value: <span className={styles.typeText}>{ticket.type}</span>,
                        }
                      : null,
                    ticket.assigned && ticket.assigned !== "-"
                      ? {
                          label: "Atanan",
                          value: (
                            <span className={styles.metaInlineIcon}>
                              <User size={12} aria-hidden /> {ticket.assigned}
                            </span>
                          ),
                        }
                      : null,
                    ticket.activity
                      ? {
                          label: "Aktivite",
                          value: (
                            <span className={styles.metaInlineIcon}>
                              <Clock size={12} aria-hidden /> {ticket.activity}
                            </span>
                          ),
                        }
                      : null,
                  ].filter(Boolean)
                : null
            }
            tags={
              labelList.length > 0 ? (
                <>
                  {labelList.slice(0, 3).map((label) => (
                    <span key={label} className={styles.tagChip}>
                      {label}
                    </span>
                  ))}
                  {labelList.length > 3 ? (
                    <span className={styles.tagOverflow}>
                      +{labelList.length - 3}
                    </span>
                  ) : null}
                </>
              ) : null
            }
            footer={
              density === "comfortable" ? (
                <>
                  {ticket.project && ticket.project !== "-" ? (
                    <span className={styles.sourceTag}>{ticket.project}</span>
                  ) : (
                    <span />
                  )}
                  <span className={styles.idLabel}>#{ticket.id}</span>
                </>
              ) : null
            }
          />
        );
      })}
    </EntityGrid>
  );
};

export default TicketCardGrid;
